import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { isNil } from 'lodash';
import { useTranslation } from '@lib/useTypedTranslation';
import { ITableColumn } from '../../../../components/data-table/types';
import { formatDateLastUpdated, formatValue, getFormatNumberToLocale, getFormatZeroAsDashToLocale } from '../../../../components/data-table/dataFormatters';
import { useWorldSettingsContext } from '../../../../context/worldSettings';
import { useWorldRoutes } from '../../../../routes/parts/allWorldRoutes';
import { rearrangeList } from '../../../../lib/rearrangeList';
import { encode } from '../../../../lib/base64';
import { useLanguageAndLocaleContext } from '../../../../context/languageAndLocale';
import { IDeviceAnomalyData } from '../../../../services/core/deviceAnomaliesList';
import { buildAnomalyExaminationPath } from '../anomalyExaminationPath';
import { GroupListFilter, HomeLocationsListFilter, ManufacturerListFilter, ModelListFilter, PlatformListFilter } from './filters/deviceAnomaliesFilters';
import { AnomalyType, anomalyTypesArray } from './anomalyTypePicker';
import { Tooltip } from '../../../../components/tooltip/tooltip';
import styled from 'styled-components';

const ScoreTooltipPopover = styled.div`
  max-width: 16rem;
`;

// A device "has a single anomaly type" (AC1) when exactly one of the six per-type counts is
// positive AND that count accounts for the whole aggregate totalCount - the second check matters
// because a type-filtered response only ever returns a real count for the selected type (every
// other type comes back as 0, see services/core/deviceAnomaliesList.ts's anomalyType filter), so
// a device with several anomaly types would otherwise look single-type whenever the table happens
// to be filtered down to one of them.
function getSingleAnomalyType(rowData: IDeviceAnomalyData): AnomalyType | undefined {
  const positiveTypes = anomalyTypesArray.filter((type) => typeof rowData[type] === 'number' && rowData[type] > 0);
  if (positiveTypes.length !== 1) {
    return undefined;
  }
  const [singleType] = positiveTypes;
  return rowData[singleType] === rowData.totalCount ? singleType : undefined;
}

interface UseDeviceAnomaliesListColumnsOptions {
  hasWindows: boolean;
  hasAndroid: boolean;
  selectedAnomalyType?: AnomalyType | null;
}

export const useDeviceAnomaliesListColumns = ({ hasWindows, hasAndroid, selectedAnomalyType }: UseDeviceAnomaliesListColumnsOptions) => {
  const { t } = useTranslation(['translation', 'anomalyDetectionBeta']);
  const { worldSettings } = useWorldSettingsContext();
  const routes = useWorldRoutes();
  const { locale } = useLanguageAndLocaleContext();

  const columns = useMemo(() => {
    // Links straight to this device's Detailed Examination page when it has only a single
    // anomaly type recorded (AC1); otherwise falls back to today's Charts page link.
    function getDeviceIdLinkProcessor(value: any, rowData: IDeviceAnomalyData) {
      if (isNil(rowData)) {
        return formatValue(null);
      }
      const singleType = getSingleAnomalyType(rowData);
      const to = singleType
        ? buildAnomalyExaminationPath(routes.core.deviceAnomalyExamination, { deviceId: rowData.id, type: singleType })
        : `${routes.core.deviceAnomalyDetails}?id=${encodeURIComponent(encode(rowData.id))}`;
      return <Link to={to} className='device-anomalies-list_link'>{formatValue(value)}</Link>;
    }

    // Each per-type count column (AC2) links directly to that type's Detailed Examination page
    // when it has a positive count - the examination page itself defaults to the latest day
    // flagged as an anomaly for that device/type (see getLatestAnomalyDate), so no date needs to
    // be threaded through here. A non-positive count keeps today's plain dash/number, since there
    // is nothing to examine.
    function getAnomalyCountLinkProcessor(type: AnomalyType) {
      const formatCount = getFormatZeroAsDashToLocale(locale);
      return (value: any, rowData: IDeviceAnomalyData) => {
        const formatted = formatCount(value);
        const isPositiveCount = typeof value === 'number' && value > 0;
        if (isNil(rowData) || !isPositiveCount) {
          return formatted;
        }
        return (
          <Link to={buildAnomalyExaminationPath(routes.core.deviceAnomalyExamination, { deviceId: rowData.id, type })} className='device-anomalies-list_link'>
            {formatted}
          </Link>
        );
      };
    }

    const showType = (type: AnomalyType) => !selectedAnomalyType || selectedAnomalyType === type;

    // Identifier columns placed last so rearrangeList moves the primary to the front
    // while the remaining identifiers stay at the end.
    const scoreTitle = (
      <>
        {t('SCORE', { ns: 'anomalyDetectionBeta' })}
        <Tooltip
          popoverPortalSelector='.device-anomalies-list'
          placement='bottom'
          popperContent={<ScoreTooltipPopover>{t('SCORE_TOOLTIP', { ns: 'anomalyDetectionBeta' })}</ScoreTooltipPopover>}
        >
          <i aria-label='Score tooltip' className='info-popover-icon fa fa-question-circle' />
        </Tooltip>
      </>
    );

    const cols: ITableColumn[] = [
      { id: 'platformType', title: t('PLATFORM', { ns: 'anomalyDetectionBeta' }), dataPath: 'platformType', sortable: true, processor: formatValue, FilterForm: PlatformListFilter },
      ...(!selectedAnomalyType ? [{ id: 'totalCount', title: t('TOTAL_COUNT', { ns: 'anomalyDetectionBeta' }), dataPath: 'totalCount', sortable: true, processor: getFormatNumberToLocale(locale), textAlign: 'right' as const }] : []),
      { id: 'score', title: scoreTitle, dataPath: 'score', sortable: true, processor: getFormatNumberToLocale(locale, 2), textAlign: 'right' },
      ...(showType('totalDataUsage') ? [{ id: 'totalDataUsage', title: t('DATA_USAGE', { ns: 'anomalyDetectionBeta' }), dataPath: 'totalDataUsage', sortable: true, processor: getAnomalyCountLinkProcessor('totalDataUsage'), textAlign: 'right' as const }] : []),
      ...(showType('deviceRebooted') ? [{ id: 'deviceRebooted', title: t('REBOOTS', { ns: 'anomalyDetectionBeta' }), dataPath: 'deviceRebooted', sortable: true, processor: getAnomalyCountLinkProcessor('deviceRebooted'), textAlign: 'right' as const }] : []),
      ...(showType('deviceLowPower') ? [{ id: 'deviceLowPower', title: t('LOW_POWER', { ns: 'anomalyDetectionBeta' }), dataPath: 'deviceLowPower', sortable: true, processor: getAnomalyCountLinkProcessor('deviceLowPower'), textAlign: 'right' as const }] : []),
      ...(hasAndroid && showType('deviceDropped') ? [{ id: 'deviceDropped', title: t('DROPS', { ns: 'anomalyDetectionBeta' }), dataPath: 'deviceDropped', sortable: true, processor: getAnomalyCountLinkProcessor('deviceDropped'), textAlign: 'right' as const }] : []),
      ...(hasWindows && showType('processCrashed') ? [{ id: 'processCrashed', title: t('PROCESS_CRASHES', { ns: 'anomalyDetectionBeta' }), dataPath: 'processCrashed', sortable: true, processor: getAnomalyCountLinkProcessor('processCrashed'), textAlign: 'right' as const }] : []),
      ...(hasWindows && showType('processHung') ? [{ id: 'processHung', title: t('PROCESS_HANGS', { ns: 'anomalyDetectionBeta' }), dataPath: 'processHung', sortable: true, processor: getAnomalyCountLinkProcessor('processHung'), textAlign: 'right' as const }] : []),
      { id: 'lastOccurred', title: t('LAST_OCCURRED', { ns: 'anomalyDetectionBeta' }), dataPath: 'lastOccurred', sortable: true, processor: formatDateLastUpdated },
      { id: 'manufacturer', title: t('MANUFACTURER', { ns: 'translation' }), dataPath: 'manufacturer', sortable: true, processor: formatValue, FilterForm: ManufacturerListFilter },
      { id: 'model', title: t('MODEL', { ns: 'translation' }), dataPath: 'model', sortable: true, processor: formatValue, FilterForm: ModelListFilter, minWidth: '8rem' },
      { id: 'group', title: t('GROUPS', { ns: 'translation' }), dataPath: 'group', processor: formatValue, FilterForm: GroupListFilter, minWidth: '10rem' },
      { id: 'homeLocation', title: t('HOME_LOCATION', { ns: 'translation' }), dataPath: 'homeLocation.name', processor: formatValue, FilterForm: HomeLocationsListFilter },
      // Identifiers at the end: rearrangeList promotes the configured primary to index 0
      { id: 'serialNumber', title: t('SERIAL_NUMBER', { ns: 'translation' }), dataPath: 'serialNumber', sortable: true, processor: formatValue },
      { id: 'assetTag', title: t('ASSET_TAG', { ns: 'translation' }), dataPath: 'assetTag', sortable: true, processor: formatValue, minWidth: '10rem' },
      { id: 'imei', title: t('IMEI', { ns: 'translation' }), dataPath: 'imei', sortable: true, processor: formatValue, minWidth: '10rem' },
      ...(hasWindows ? [{ id: 'deviceName', title: t('DEVICE_NAME', { ns: 'translation' }), dataPath: 'deviceName', sortable: true, processor: formatValue }] : []),
    ];

    const deviceIdentifier = worldSettings.deviceIdentifier;
    const updatedColumns = rearrangeList(cols, deviceIdentifier);
    updatedColumns[0] = { ...updatedColumns[0], processor: getDeviceIdLinkProcessor };

    return updatedColumns;
  }, [hasWindows, hasAndroid, selectedAnomalyType, worldSettings, routes.core.deviceAnomalyDetails, routes.core.deviceAnomalyExamination, t, locale]);

  return columns;
};
